import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { RadioButton, RadioButtonProps, RadioButtonTheme } from '..';
import { classes } from './RadioButton.visual.st.css';
import { ErrorAppearance } from '../internal/ErrorMessageWrapper';
import { EditorShell, VisualAsync } from '../../test/visual/VisualAsync';
import { radioButtonDriverFactory } from './RadioButton.driver';

const VisualRadioButton = (props: Partial<RadioButtonProps>) => (
  <EditorShell>
    <RadioButton
      label={props.label || 'label'}
      value="value"
      onChange={() => {}}
      {...props}
    />
  </EditorShell>
);

visualize('RadioButton', () => {
  story('default', () => {
    Object.values(RadioButtonTheme).map((theme) => {
      snap(`${theme} / default`, <VisualRadioButton theme={theme} />);
    });
  });

  story('checked', () => {
    Object.values(RadioButtonTheme).map((theme) => {
      snap(`${theme} / checked`, <VisualRadioButton theme={theme} checked />);
    });
  });

  {
    function getFocusWithStyleOverridesTests() {
      return [
        {
          describe: 'focused / new with hover overrides',
          its: Object.values(RadioButtonTheme).flatMap((theme) => [
            {
              it: `${theme} / unchecked`,
              props: {
                theme,
                className: classes.hoverOverrides,
                subtext: 'subtext here',
              },
            },
            {
              it: `${theme} / checked`,
              props: {
                theme,
                checked: true,
                className: classes.hoverOverrides,
                subtext: 'subtext here',
              },
            },
          ]),
        },
      ];
    }

    function getFocusTests() {
      const its = Object.values(RadioButtonTheme).flatMap((theme) => {
        const errorProps = { error: true, errorMessage: 'some error' };

        return [
          {
            it: `${theme} / unchecked`,
            props: { theme, subtext: 'some subtext' },
          },
          {
            it: `${theme} / unchecked with default error`,
            props: {
              theme,
              subtext: 'some subtext',
              ...errorProps,
            },
          },
          {
            it: `${theme} / unchecked with not new error`,
            props: {
              theme,
              subtext: 'some subtext',
              ...errorProps,
              newErrorMessage: false,
            },
          },
          {
            it: `${theme} / unchecked with new error`,
            props: {
              theme,
              subtext: 'some subtext',
              error: true,
              ...errorProps,
              newErrorMessage: true,
            },
          },
          {
            it: `${theme} / checked`,
            props: { theme, subtext: 'some subtext', checked: true },
          },
          {
            it: `${theme} / checked with default error`,
            props: {
              theme,
              subtext: 'some subtext',
              ...errorProps,
              checked: true,
            },
          },
          {
            it: `${theme} / checked with not new error`,
            props: {
              theme,
              subtext: 'some subtext',
              ...errorProps,
              newErrorMessage: false,
              checked: true,
            },
          },
          {
            it: `${theme} / checked with new error`,
            props: {
              theme,
              subtext: 'some subtext',
              error: true,
              ...errorProps,
              newErrorMessage: true,
              checked: true,
            },
          },
        ];
      });

      const tests = [{ describe: 'focused / new default', its }];

      return tests;
    }

    [...getFocusWithStyleOverridesTests(), ...getFocusTests()].forEach(
      ({ describe, its }) =>
        story(describe, () => {
          its.forEach(({ it, props }) => {
            snap(it, (done) => (
              <VisualAsync
                driverFactory={radioButtonDriverFactory}
                onDone={done}
                action={(driver) => driver.clickInput()}
              >
                <RadioButton
                  label="label"
                  value="value"
                  onChange={() => {}}
                  {...props}
                />
              </VisualAsync>
            ));
          });
        }),
    );
  }

  story('disabled', () => {
    Object.values(RadioButtonTheme).map((theme) => {
      snap(`${theme} / disabled`, <VisualRadioButton theme={theme} disabled />);
      snap(
        `${theme} / disabled / checked`,
        <VisualRadioButton theme={theme} disabled checked />,
      );
    });
  });

  story('error', () => {
    snap(
      `${RadioButtonTheme.Default} / error`,
      <VisualRadioButton theme={RadioButtonTheme.Default} error />,
    );
    snap(
      `${RadioButtonTheme.Default} / new error`,
      <VisualRadioButton
        newErrorMessage
        error
        errorMessage="An error occurred."
      />,
    );
    snap(
      `${RadioButtonTheme.Default} / new error / ErrorAppearance - BackgroundAndBorder`,
      <VisualRadioButton
        newErrorMessage
        errorAppearance={ErrorAppearance.BackgroundAndBorder}
        error
        errorMessage="An error occurred."
      />,
    );
  });

  story('with children prop', () => {
    snap(
      `${RadioButtonTheme.Default} / default`,
      <VisualRadioButton
        theme={RadioButtonTheme.Default}
        children={
          <>
            <div>Using children as the RadioButton label</div>
            <div>Can add a subtitle as well</div>
          </>
        }
      />,
    );
  });

  story('with subtext prop', () => {
    Object.values(RadioButtonTheme).map((theme) => {
      snap(
        `${theme} / default`,
        <VisualRadioButton theme={theme} subtext="This is some subtext" />,
      );
    });
  });

  story('with subtext prop and override styles', () => {
    Object.values(RadioButtonTheme).map((theme) => {
      snap(
        `${theme} / default`,
        <VisualRadioButton
          theme={theme}
          subtext="This is some subtext"
          className={classes.root}
        />,
      );
      snap(
        `${theme} / checked`,
        <VisualRadioButton
          checked
          theme={theme}
          subtext="This is some subtext"
          className={classes.root}
        />,
      );
      snap(
        `${theme} / error`,
        <div>
          <VisualRadioButton
            newErrorMessage
            checked
            error
            errorMessage="Not very good, pretty bad actually."
            theme={theme}
            subtext="This is some subtext"
            className={classes.root}
          />
          <span>End of error message.</span>
        </div>,
      );
      snap(
        `${theme} / error / ErrorAppearance - BackgroundAndBorder`,
        <div>
          <VisualRadioButton
            newErrorMessage
            errorAppearance={ErrorAppearance.BackgroundAndBorder}
            checked
            error
            errorMessage="Not very good, pretty bad actually."
            theme={theme}
            subtext="This is some subtext"
            className={classes.root}
          />
          <span>End of error message.</span>
        </div>,
      );
    });
  });

  story('long', () => {
    snap(
      `${RadioButtonTheme.Box} / long`,
      <VisualRadioButton
        theme={RadioButtonTheme.Box}
        label="It is a period of civil war. Rebel spaceships, striking from a hidden base, have won their first victory against the evil Galactic Empire. During the battle, Rebel spies managed to steal secret plans to the Empire's ultimate weapon, the DEATH STAR, an armored space station with enough power to destroy an entire planet."
      />,
    );
    snap(
      `${RadioButtonTheme.Box} / long / suffix`,
      <VisualRadioButton
        theme={RadioButtonTheme.Box}
        suffix="$50,000"
        label="It is a period of civil war. Rebel spaceships, striking from a hidden base, have won their first victory against the evil Galactic Empire. During the battle, Rebel spies managed to steal secret plans to the Empire's ultimate weapon, the DEATH STAR, an armored space station with enough power to destroy an entire planet."
      />,
    );
  });

  story('inline spacing', () => {
    Object.values(RadioButtonTheme).map((theme) => {
      snap(
        `${theme} / increased`,
        <VisualRadioButton theme={theme} className={classes.inlineSpacing} />,
      );
    });
  });
  story('with wix site theme variables', () => {
    snap('error state', () => {
      const style = {
        '--wst-system-error-color-rgb': '0,150,255',
      } as React.CSSProperties;

      return (
        <div style={style}>
          <VisualRadioButton
            label="box error disabled checked"
            theme={RadioButtonTheme.Box}
            subtext="This is some subtext"
            checked
            disabled
            error
          />
          <VisualRadioButton
            label="box error disabled"
            theme={RadioButtonTheme.Box}
            subtext="This is some subtext"
            disabled
            error
          />
          <VisualRadioButton
            label="box error checked"
            theme={RadioButtonTheme.Box}
            subtext="This is some subtext"
            checked
            error
          />
          <VisualRadioButton
            label="box error"
            theme={RadioButtonTheme.Box}
            subtext="This is some subtext"
            error
          />

          <VisualRadioButton
            label="error disabled checked"
            theme={RadioButtonTheme.Default}
            subtext="This is some subtext"
            checked
            disabled
            error
          />
          <VisualRadioButton
            label="error disabled"
            theme={RadioButtonTheme.Default}
            subtext="This is some subtext"
            disabled
            error
          />
          <VisualRadioButton
            label="error checked"
            theme={RadioButtonTheme.Default}
            subtext="This is some subtext"
            checked
            error
          />
          <VisualRadioButton
            label="error"
            theme={RadioButtonTheme.Default}
            subtext="This is some subtext"
            error
          />
        </div>
      );
    });

    snap('boxed', () => {
      const style = {
        '--wst-primary-background-color-rgb': '45,100,0',
        '--wst-paragraph-2-color-rgb': '240,110,150',
        '--wst-paragraph-2-font-family': 'fantasy',
        '--wst-links-and-actions-color-rgb': '0,222,222',
        '--wst-system-disabled-color-rgb': '50,255,0',
      } as React.CSSProperties;

      return (
        <div style={style}>
          <VisualRadioButton
            label="box"
            theme={RadioButtonTheme.Box}
            subtext="This is some subtext"
          />
          <VisualRadioButton
            label="box checked"
            theme={RadioButtonTheme.Box}
            subtext="This is some subtext"
            checked
          />
          <VisualRadioButton
            label="box disabled"
            theme={RadioButtonTheme.Box}
            subtext="This is some subtext"
            disabled
          />
          <VisualRadioButton
            label="box disabled checked"
            theme={RadioButtonTheme.Box}
            subtext="This is some subtext"
            checked
            disabled
          />
        </div>
      );
    });

    snap('default', () => {
      const style = {
        '--wst-primary-background-color-rgb': '45,200,0',
        '--wst-paragraph-2-color-rgb': '240,110,150',
        '--wst-paragraph-2-font-family': 'fantasy',
        '--wst-links-and-actions-color-rgb': '0,222,222',
        '--wst-shade-3-color-rgb': '0,10,222',
        '--wst-system-disabled-color-rgb': '50,255,0',
        '--wst-paragraph-2-font-size': '22px',
        '--wst-paragraph-2-font-weight': 'bold',
        '--wst-paragraph-2-font-style': 'italic',
        '--wst-paragraph-2-font-variant': 'small-caps',
      } as React.CSSProperties;

      return (
        <div style={style}>
          <VisualRadioButton
            label="default"
            theme={RadioButtonTheme.Default}
            subtext="This is some subtext"
          />
          <VisualRadioButton
            label="default checked"
            theme={RadioButtonTheme.Default}
            subtext="This is some subtext"
            checked
          />
          <VisualRadioButton
            label="default disabled"
            theme={RadioButtonTheme.Default}
            subtext="This is some subtext"
            disabled
          />
          <VisualRadioButton
            label="disabled checked"
            theme={RadioButtonTheme.Default}
            subtext="This is some subtext"
            checked
            disabled
          />
        </div>
      );
    });
  });
});
